import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { Box, Button, Dialog } from "@primer/react";
import { useCommonContext } from "../../../contexts/common";
import PhuHuynhFamily from "../../../components/phu-huynh-family";
import PhuHuynhInfo from "../../../components/phu-huynh-info";
import { quanHeGiaDinhApi } from "../../../api/quanHeGiaDinhApi";
import { NotifyHelper } from "../../../helpers/toast";
import styles from "../PhuHuynhProfile.module.css";
import PhuHuynhChildList from "../../../components/phu-huynh-child-list";
import PhuHuynhCSKH from "../../../components/phu-huynh-cskh/PhuHuynhCSKH_V2";

interface PhuHuynhProfilePageProps {
  id: string | number;
}

interface SelectedState {
  phuHuynhSelectedId: number;
  hocSinhSelectedId: number;
  hoSoSurveySelectedId: number;
}

interface QuanHeData {
  phuHuynhId: number;
  hocSinhId: number;
}

const PhuHuynhProfilePage: React.FC<PhuHuynhProfilePageProps> = ({ id }) => {
  const { translate } = useCommonContext();
  const location = useLocation();
  const [quanhes, setQuanHes] = useState([]);

  const [deleteModalState, setDeleteModalState] = React.useState<{
    isOpen: boolean;
    quanHeData: QuanHeData | null;
  }>({
    isOpen: false,
    quanHeData: null,
  });

  const [selected, setSelected] = React.useState<SelectedState>(() => ({
    phuHuynhSelectedId: Number(id),
    hocSinhSelectedId: 0,
    hoSoSurveySelectedId: 0,
  }));

  const handleSelectedChange = useCallback(
    (newState: Partial<SelectedState>) => {
      setSelected((prev) => ({
        ...prev,
        ...newState,
      }));
    },
    []
  );

  const handleFamilyMemberSelect = useCallback((memberId: number) => {
    setSelected({
      phuHuynhSelectedId: memberId,
      hocSinhSelectedId: 0,
      hoSoSurveySelectedId: 0,
    });
  }, []);

  const handleDeleteQuanHe = useCallback(
    async (data: QuanHeData) => {
      try {
        const response = await quanHeGiaDinhApi.deleteQHGD(data);
        if (response.is_success) {
          NotifyHelper.Success(translate("Success"));
          if (data.hocSinhId === selected.hocSinhSelectedId) {
            handleSelectedChange({
              hocSinhSelectedId: 0,
              hoSoSurveySelectedId: 0,
            });
          }
        } else {
          NotifyHelper.Error(response.message || translate("Error occurred"));
        }
      } catch (error) {
        console.error("Delete QHGD error:", error);
        NotifyHelper.Error(translate("Error occurred"));
      } finally {
        setDeleteModalState({ isOpen: false, quanHeData: null });
      }
    },
    [selected.hocSinhSelectedId, translate]
  );

  const containerHeight = useMemo(() => window.innerHeight - 300, []);

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "1fr 600px",
        gap: 1,
        height: containerHeight,
        maxWidth: "100%",
      }}
    >
      <Box
        sx={{
          overflow: "auto",
          borderRight: "1px solid",
          borderColor: "border.default",
          pr: 0,
        }}
      >
        <Box className={styles.phuhuynh_family}>
          <PhuHuynhInfo
            id={selected.phuHuynhSelectedId} 
            isSelected={true} 
            onSelected={() => handleFamilyMemberSelect(Number(id))}
          />
        </Box>

        <Box className={styles.phuhuynh_family}>
          <PhuHuynhFamily
            id={selected.phuHuynhSelectedId} 
            onMemberSelect={handleFamilyMemberSelect}
          />
        </Box>

        <Box className={styles.phuhuynh_childlist}>
          <PhuHuynhChildList
            id={selected.phuHuynhSelectedId}
            hocSinhSelectedId={selected.hocSinhSelectedId}
            onSelected={(hocSinhId) =>
              handleSelectedChange({
                hocSinhSelectedId: hocSinhId,
                hoSoSurveySelectedId: -1,
              })
              
            }
            onDeleted={(phuHuynhId, hocSinhId) => {
              setDeleteModalState({
                isOpen: true,
                quanHeData: {
                  phuHuynhId: phuHuynhId,
                  hocSinhId: hocSinhId,
                },
              });
            }}
          />
        </Box>
      </Box>

      <Box
        sx={{
          overflow: "auto",
          backgroundColor: "canvas.subtle",
          borderRadius: 2,
          p: 3,
        }}
      >
        <PhuHuynhCSKH
          ts_phuhuynh_id={selected.phuHuynhSelectedId}
          ts_hocsinh_id={selected.hocSinhSelectedId}
          ts_hoso_survey_id={selected.hoSoSurveySelectedId}
        />
      </Box>

      <Dialog
        isOpen={deleteModalState.isOpen}
        onDismiss={() =>
          setDeleteModalState({ isOpen: false, quanHeData: null })
        }
        title={translate("Notice")}
      >
        <Box p={3}>
          <Box mb={3}>
            {translate("Are you sure you want to delete this student?")}
          </Box>
          <Box display="flex" justifyContent="flex-end">
            <Button
              onClick={() =>
                setDeleteModalState({ isOpen: false, quanHeData: null })
              }
              sx={{ mr: 2 }}
            >
              {translate("Close")}
            </Button>
            <Button
              variant="danger"
              onClick={() =>
                deleteModalState.quanHeData &&
                handleDeleteQuanHe(deleteModalState.quanHeData)
              }
            >
              {translate("Delete Student")}
            </Button>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
};

export default PhuHuynhProfilePage;
